import { useState, type ReactNode } from "react";
import { UserCog } from "lucide-react";
import { LoadingScreen } from "@/components/LoadingScreen/LoadingScreen";
import { getImpersonationSession } from "@/features/impersonation/impersonation-session";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { User } from "@/common/types/user";
import {
  OrganizationTermsAgreement,
  type TermsStatus,
} from "./organization-terms-agreement";
import { useOrganizationTerms } from "./use-organization-terms";

type OrganizationTermsGateProps = {
  organizationId: string;
  user: Pick<User, "first_name" | "last_name" | "is_admin">;
  children: ReactNode;
};

function agreementKey(
  organizationId: string,
  agreement: NonNullable<TermsStatus["agreement"]>,
): string {
  return JSON.stringify([
    organizationId,
    agreement.id,
    agreement.version,
    agreement.url,
  ]);
}

export function OrganizationTermsGate({
  organizationId,
  user,
  children,
}: OrganizationTermsGateProps) {
  // An impersonation session only starts or ends with a full page load.
  const [impersonation] = useState(getImpersonationSession);
  // Staff dismissals are scoped to one agreement revision for one organization,
  // so a newly published version or an organization switch prompts again.
  const [dismissedAgreementKey, setDismissedAgreementKey] = useState<
    string | null
  >(null);
  const hasCompletedAccount = Boolean(
    user.first_name?.trim() && user.last_name?.trim(),
  );
  // Staff accounts never sign for a customer, so they are never gated. The
  // server also refuses them; this keeps a stale organization list harmless.
  const enabled = Boolean(
    organizationId && hasCompletedAccount && !user.is_admin,
  );
  // Keep checking after acceptance so active sessions see newly published terms.
  const termsQuery = useOrganizationTerms({
    organizationId,
    enabled,
    recheckPeriodically: true,
  });
  const status = termsQuery.data;
  if (!enabled) {
    return <>{children}</>;
  }

  // Keep the organization UI inaccessible until the server identifies whether
  // acceptance is required. A neutral screen avoids flashing a consent modal
  // for users whose organization has already accepted or who cannot sign.
  if (!status) {
    if (!termsQuery.isError) {
      return <LoadingScreen />;
    }

    return (
      <div className="flex min-h-dvh w-full items-center justify-center bg-gray-100 p-6">
        <div className="max-w-md space-y-4 text-center">
          <p role="alert" className="text-sm text-destructive">
            We couldn't load your organization. Please try again.
          </p>
          <Button
            onClick={() => void termsQuery.refetch()}
            disabled={termsQuery.isFetching}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // A failed background refresh does not revoke a confirmed acceptance or turn
  // a known non-signer into a signer.
  if (!status.agreement || !status.canAccept || status.acceptance) {
    return <>{children}</>;
  }

  const currentAgreementKey = agreementKey(organizationId, status.agreement);
  if (impersonation && dismissedAgreementKey === currentAgreementKey) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <Dialog open>
        <DialogContent
          className="w-full max-w-xl max-h-[90dvh] overflow-y-auto gap-5"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader className="pr-6">
            <DialogTitle>
              Review your organization's Terms of Service
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              An authorized organization administrator must accept the current
              terms before continuing.
            </DialogDescription>
          </DialogHeader>
          {termsQuery.isError && (
            <div className="space-y-3">
              <p role="alert" className="text-sm text-destructive">
                We couldn't check your organization's terms. Please try again to
                continue.
              </p>
              <Button
                onClick={() => void termsQuery.refetch()}
                disabled={termsQuery.isFetching}
              >
                Retry
              </Button>
            </div>
          )}
          {termsQuery.isSuccess && status?.agreement && (
            <>
              <p className="text-sm text-muted-foreground">
                Accepting on behalf of {status.organizationName}.
              </p>
              <OrganizationTermsAgreement
                key={currentAgreementKey}
                status={status}
                agreement={status.agreement}
              />
            </>
          )}
          {impersonation && (
            <div
              role="note"
              className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <UserCog className="mt-1 size-5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-medium">
                    Impersonating {impersonation.displayName}
                  </p>
                  <p>
                    Only an administrator of {status.organizationName} can
                    accept these terms.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="shrink-0 border-amber-300 bg-white hover:bg-amber-100"
                onClick={() => setDismissedAgreementKey(currentAgreementKey)}
              >
                Continue without accepting
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
